import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import {
  faSpinner,
  faExclamationCircle,
  faEllipsisV,
  faCalendar,
  faClock,
  faCog,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

export interface ListItemTag {
  id: number | string;
  name: string;
  bg_color: string;
  text_color: string;
}

export interface ListItem {
  id: number | string;
  name: string;
  tag?: ListItemTag | null;
  tags?: ListItemTag[];
  created_date?: string;
  created_time?: string;
  type?: string;
  status?: string;
  progress?: {
    current: number;
    total: number;
    percentage: number;
  };
  [key: string]: any;
}

export interface ListAction {
  id: string;
  label: string;
  icon: any;
  cssClass?: string;
  visible?: (item: ListItem) => boolean;
  disabled?: (item: ListItem) => boolean;
}

export interface ListConfig {
  itemIcon: any;
  itemIconClass?: string;
  showMetadata?: boolean;
  showType?: boolean;
  typePosition?: 'metadata' | 'inline';
  typeLabels?: { [key: string]: string };
  emptyMessage?: string;
  emptyMessageFiltered?: string;
  actions: ListAction[];
  showCheckboxes?: boolean;
  showSelectCheckboxes?: boolean;
  checkboxVisible?: (item: ListItem) => boolean;
  checkboxDisabled?: (item: ListItem) => boolean;
  showConfigSummary?: boolean;
}

@Component({
  selector: 'app-item-list',
  standalone: true,
  imports: [FontAwesomeModule, TranslateModule],
  templateUrl: './item-list.component.html',
  styleUrls: ['./item-list.component.scss']
})
export class ItemListComponent implements OnChanges {
  @Input() items: ListItem[] = [];
  @Input() loading = false;
  @Input() config!: ListConfig;
  @Input() hasActiveFilters = false;
  @Input() showSubname: boolean = false;
  @Input() selectedItems: Set<string | number> = new Set();

  @Output() itemClick = new EventEmitter<ListItem>();
  @Output() actionClick = new EventEmitter<{ action: string; item: ListItem; event: Event }>();
  @Output() itemSelectionChanged = new EventEmitter<{ item: ListItem; selected: boolean }>();
  @Output() tagClick = new EventEmitter<any>();

  activeDropdown: number | null = null;
  expandedConfigItemIds: Set<string | number> = new Set();

  faSpinner = faSpinner;
  faExclamationCircle = faExclamationCircle;
  faEllipsisV = faEllipsisV;
  faCalendar = faCalendar;
  faClock = faClock;
  faCog = faCog;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  ngOnChanges(changes: SimpleChanges): void {
  }

  get showEmptyState(): boolean {
    return !this.loading && this.items.length === 0;
  }

  get emptyStateMessage(): string {
    if (this.hasActiveFilters) {
      return this.config.emptyMessageFiltered || 'MESSAGE.NO.RESULTS.SEARCH';
    }
    return this.config.emptyMessage || 'MESSAGE.NO.RESULTS';
  }

  trackByItem(index: number, item: ListItem) {
    return item.id;
  }

  onItemClick(item: ListItem): void {
    this.itemClick.emit(item);
  }

  onTagClick(tag: any, event: Event): void {
    event.stopPropagation();
    this.tagClick.emit(tag);
  }

  toggleDropdown(index: number, event: Event): void {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === index ? null : index;
  }

  onActionClick(action: ListAction, item: ListItem, event: Event): void {
    event.stopPropagation();
    this.activeDropdown = null;
    this.actionClick.emit({ action: action.id, item, event });
  }

  isActionVisible(action: ListAction, item: ListItem): boolean {
    return action.visible ? action.visible(item) : true;
  }

  isActionDisabled(action: ListAction, item: ListItem): boolean {
    return action.disabled ? action.disabled(item) : false;
  }

  getTypeLabel(type: string): string {
    return this.config.typeLabels?.[type] || type;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: Event): void {
    const target = ev.target as HTMLElement;
    if (target.closest('.dropdown-container')) return;
    this.activeDropdown = null;
  }

  onCheckboxChange(item: ListItem, event: Event): void {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    this.itemSelectionChanged.emit({ item, selected: checkbox.checked });
  }

  isItemSelected(item: ListItem): boolean {
    return this.selectedItems.has(item.id);
  }

  isSelectCheckboxVisible(item: ListItem): boolean {
    return this.config.checkboxVisible ? this.config.checkboxVisible(item) : true;
  }

  isSelectCheckboxDisabled(item: ListItem): boolean {
    return this.config.checkboxDisabled ? this.config.checkboxDisabled(item) : false;
  }

  isConfigExpanded(item: ListItem): boolean {
    return this.expandedConfigItemIds.has(item.id);
  }

  toggleConfigSummary(item: ListItem, event: Event): void {
    event.stopPropagation();
    const next = new Set(this.expandedConfigItemIds);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    this.expandedConfigItemIds = next;
  }
}